import type { Summary, Timeseries } from "@/data/services";
import type { Kpi, Point, Trend } from "@/mock";

/** Hour label for a unix-second timestamp (matches the sample HH labels). */
function hourLabel(tSeconds: number): string {
  const d = new Date(tSeconds * 1000);
  return `${d.getHours().toString().padStart(2, "0")}`;
}

/** Real timeseries → chart Points. Falls back to the sample series shape. */
export function pointsFromTimeseries(ts: Timeseries | undefined): Point[] {
  if (!ts || ts.points.length === 0) return [];
  return ts.points.map((p) => ({ t: hourLabel(p.t), v: Math.max(0, Math.round(p.value)) }));
}

function trendFor(delta: number): Trend {
  return delta > 0 ? "up" : delta < 0 ? "down" : "flat";
}

/**
 * Map a real Summary (+ the visits timeseries for sparklines) onto the four
 * headline Today KPIs. The backend has no period-over-period delta yet, so
 * deltas are derived from the visits series (last vs first half) where we can,
 * and left at 0 otherwise — honest, not invented.
 */
export function kpisFromSummary(summary: Summary, visitsSeries: Point[]): Kpi[] {
  const conversions = Math.round(summary.conversion_rate * summary.visits);

  // crude but honest momentum from the visits series itself
  const half = Math.floor(visitsSeries.length / 2);
  const firstHalf = visitsSeries.slice(0, half).reduce((a, p) => a + p.v, 0) || 1;
  const lastHalf = visitsSeries.slice(half).reduce((a, p) => a + p.v, 0);
  const visitsDelta =
    visitsSeries.length >= 2 ? Math.round(((lastHalf - firstHalf) / firstHalf) * 1000) / 10 : 0;

  const scaled = (factor: number): Point[] =>
    visitsSeries.length > 0
      ? visitsSeries.map((p) => ({ t: p.t, v: Math.max(0, Math.round(p.v * factor)) }))
      : [];

  const convFactor = summary.visits > 0 ? conversions / summary.visits : 0;

  return [
    {
      key: "visitors",
      label: "Visitors today",
      value: summary.visits,
      unit: "count",
      delta: visitsDelta,
      trend: trendFor(visitsDelta),
      series: visitsSeries,
      source: "posthog",
      focal: true,
    },
    {
      key: "conversions",
      label: "Conversions",
      value: conversions,
      unit: "count",
      delta: 0,
      trend: "flat",
      series: scaled(convFactor),
      source: "posthog",
    },
    {
      key: "convrate",
      label: "Conversion rate",
      value: Math.round(summary.conversion_rate * 1000) / 10,
      unit: "pct",
      delta: 0,
      trend: "flat",
      series: scaled(convFactor),
      source: "matomo",
    },
    {
      key: "avgtime",
      label: "Avg. time on site",
      value: Math.round(summary.avg_session_seconds),
      unit: "sec",
      delta: 0,
      trend: "flat",
      series: visitsSeries,
      source: "matomo",
    },
  ];
}
